'use client'

import {
    startChatWithTemplate,
    StartChatWithTemplateData,
} from '@/lib/api/atendimento/chat'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import { Client, getClientsByOrganization } from '@/lib/api/clients/client-api'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { useDebounce } from '@/lib/hooks/use-debounce'
import {
    getSendableWhatsappTemplates,
    WhatsappTemplate,
} from '@/lib/api/messaging/whatsapp-templates-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MdClose, MdPersonAdd, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'

interface Department {
    id: number
    name: string
    whatsappNumber?: string | null
    whatsappProvider?: 'WHATSAPP_API' | 'WHATSAPP_WEB'
}

interface StartChatPrefill {
    phoneNumber?: string
    name?: string
    departmentId?: number
}

interface Props {
    isOpen: boolean
    onClose: () => void
    onStarted: (chatId: number, reused: boolean) => void
    // Pré-preenche e trava contato/departamento (ex.: reabertura de um chat).
    prefill?: StartChatPrefill
    mode?: 'new' | 'reopen'
}

const PHONE_DIGITS_RE = /\D+/g

const extractVars = (body: string): number[] => {
    const matches = Array.from(body.matchAll(/\{\{\s*(\d+)\s*\}\}/g))
    const set = new Set<number>()
    for (const m of matches) set.add(parseInt(m[1], 10))
    return Array.from(set).sort((a, b) => a - b)
}

export default function StartChatModal({
    isOpen,
    onClose,
    onStarted,
    prefill,
    mode = 'new',
}: Props) {
    const { user } = useAuth()
    const organizationId = user?.organizationId
    const isReopen = mode === 'reopen' || !!prefill

    const [phoneNumber, setPhoneNumber] = useState('')
    const [name, setName] = useState('')
    const [departmentId, setDepartmentId] = useState<number | null>(null)
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
        null
    )
    const [templateVars, setTemplateVars] = useState<Record<string, string>>({})
    const [freeText, setFreeText] = useState('')

    const [departments, setDepartments] = useState<Department[]>([])
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([])
    const [loadingDeps, setLoadingDeps] = useState(false)
    const [loadingTpls, setLoadingTpls] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<Client[]>([])
    const [searching, setSearching] = useState(false)
    const [showResults, setShowResults] = useState(false)
    const [selectedClient, setSelectedClient] = useState<Client | null>(null)
    const debouncedSearch = useDebounce(searchInput, 300)
    const searchBoxRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!isOpen) return
        setPhoneNumber(prefill?.phoneNumber ?? '')
        setName(prefill?.name ?? '')
        setDepartmentId(prefill?.departmentId ?? null)
        setSelectedTemplateId(null)
        setTemplateVars({})
        setFreeText('')
        setSearchInput('')
        setSearchResults([])
        setShowResults(false)
        setSelectedClient(null)
    }, [
        isOpen,
        prefill?.phoneNumber,
        prefill?.name,
        prefill?.departmentId,
    ])

    useEffect(() => {
        if (!isOpen || !organizationId || selectedClient) {
            setSearchResults([])
            return
        }
        const term = debouncedSearch.trim()
        if (term.length < 2) {
            setSearchResults([])
            return
        }
        let cancelled = false
        setSearching(true)
        getClientsByOrganization(organizationId, { search: term, limit: 8 })
            .then(res => {
                if (cancelled) return
                if (res.success && res.result) {
                    setSearchResults(res.result.filter(c => !!c.whatsAppNumber))
                } else {
                    setSearchResults([])
                }
            })
            .finally(() => {
                if (!cancelled) setSearching(false)
            })
        return () => {
            cancelled = true
        }
    }, [debouncedSearch, isOpen, organizationId, selectedClient])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                searchBoxRef.current &&
                !searchBoxRef.current.contains(e.target as Node)
            ) {
                setShowResults(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const pickClient = (client: Client) => {
        setSelectedClient(client)
        setPhoneNumber(client.whatsAppNumber || '')
        setName(client.name)
        setSearchInput('')
        setSearchResults([])
        setShowResults(false)
    }

    const clearClient = () => {
        setSelectedClient(null)
        setPhoneNumber('')
        setName('')
    }

    useEffect(() => {
        if (!isOpen || !organizationId) return
        let cancelled = false

        const loadDeps = async () => {
            setLoadingDeps(true)
            try {
                const res = await getDepartmentsByOrganization(organizationId)
                if (cancelled) return
                if (res.success && res.result) {
                    const list = (res.result as any[]).filter(
                        d => d.whatsappNumber
                    )
                    setDepartments(list)
                    if (list.length === 1) setDepartmentId(list[0].id)
                } else {
                    toast.error(res.error || 'Erro ao carregar departamentos')
                }
            } finally {
                if (!cancelled) setLoadingDeps(false)
            }
        }

        loadDeps()
        return () => {
            cancelled = true
        }
    }, [isOpen, organizationId])

    // A lista depende do SETOR escolhido: no WhatsApp Web a aprovação da Meta
    // não é exigida, então trocar de setor pode mudar o que está disponível.
    useEffect(() => {
        if (!isOpen) return
        let cancelled = false

        const loadTpls = async () => {
            setLoadingTpls(true)
            try {
                const res = await getSendableWhatsappTemplates(departmentId)
                if (cancelled) return
                if (res.success && res.result) {
                    setTemplates(res.result)
                } else {
                    toast.error(res.error || 'Erro ao carregar templates')
                }
            } finally {
                if (!cancelled) setLoadingTpls(false)
            }
        }

        loadTpls()
        return () => {
            cancelled = true
        }
    }, [isOpen, departmentId])

    const selectedTemplate = useMemo(
        () => templates.find(t => t.id === selectedTemplateId),
        [templates, selectedTemplateId]
    )

    // O canal WhatsApp Web não tem janela de 24h: da para escrever direto, sem
    // template aprovado. A obrigatoriedade do template e uma regra da API
    // oficial (Meta), não do produto.
    const selectedDepartment = useMemo(
        () => departments.find(d => d.id === departmentId),
        [departments, departmentId]
    )
    const isWhatsappWeb =
        selectedDepartment?.whatsappProvider === 'WHATSAPP_WEB'

    // Template com variáveis semânticas ({{client.name}}...): o backend resolve
    // tudo no envio. Não pedimos preenchimento manual.
    const isTokenized = useMemo(
        () =>
            !!selectedTemplate?.bodyTemplate ||
            !!selectedTemplate?.variables?.some(v => !!v.token),
        [selectedTemplate]
    )

    const requiredVars = useMemo(
        () =>
            selectedTemplate && !isTokenized
                ? extractVars(selectedTemplate.bodyText || '')
                : [],
        [selectedTemplate, isTokenized]
    )

    const renderPreview = (): string => {
        if (!selectedTemplate) return ''
        if (isTokenized) {
            return selectedTemplate.bodyTemplate || selectedTemplate.bodyText || ''
        }
        let text = selectedTemplate.bodyText || ''
        for (const idx of requiredVars) {
            const value = templateVars[String(idx)] || `{{${idx}}}`
            text = text.replaceAll(`{{${idx}}}`, value)
        }
        return text
    }

    const phoneDigits = phoneNumber.replace(PHONE_DIGITS_RE, '')
    const baseValido =
        phoneDigits.length >= 10 &&
        name.trim().length > 0 &&
        departmentId != null
    const canSubmit = isWhatsappWeb
        ? baseValido && freeText.trim().length > 0
        : baseValido &&
          !!selectedTemplate &&
          requiredVars.every(i => templateVars[String(i)]?.trim())

    const handleSubmit = async () => {
        if (!canSubmit || !departmentId) return

        // Tokenizado: backend resolve as variáveis; envia lista vazia.
        const variables = isTokenized
            ? []
            : requiredVars.map(i => templateVars[String(i)].trim())
        const payload: StartChatWithTemplateData = isWhatsappWeb
            ? {
                  phoneNumber: phoneDigits,
                  name: name.trim(),
                  departmentId,
                  body: freeText.trim(),
              }
            : {
                  phoneNumber: phoneDigits,
                  name: name.trim(),
                  departmentId,
                  templateId: selectedTemplate!.id,
                  variables,
                  previewText: renderPreview(),
              }

        setSubmitting(true)
        try {
            const res = await startChatWithTemplate(payload)
            if (res.success && res.result) {
                if (res.result.reused) {
                    toast.info(
                        'Atendimento já existia para este número; abrindo conversa'
                    )
                } else {
                    toast.success(
                        isWhatsappWeb
                            ? 'Atendimento iniciado e mensagem enviada'
                            : 'Atendimento iniciado e template enviado'
                    )
                }
                onStarted(res.result.chatId, res.result.reused)
                onClose()
            } else {
                toast.error(res.error || 'Erro ao iniciar atendimento')
            }
        } catch (error: any) {
            console.error('Erro ao iniciar atendimento:', error)
            toast.error(
                error?.response?.data?.message || 'Erro ao iniciar atendimento'
            )
        } finally {
            setSubmitting(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-1">
                    <MdPersonAdd className="text-secondary" />{' '}
                    {isReopen ? 'Reabrir atendimento' : 'Novo atendimento'}
                </h3>
                <p className="text-xs text-base-content/60 mb-4">
                    {isWhatsappWeb
                        ? 'Este setor usa WhatsApp Web, que não tem janela de 24 horas: escreva a mensagem livremente. Se já existir atendimento aberto, ele será reaproveitado.'
                        : isReopen
                          ? 'A janela de 24h está fechada. Envie um template aprovado para reabrir este atendimento.'
                          : 'Inicie uma conversa com qualquer contato WhatsApp enviando um template aprovado. Se já existir atendimento aberto, ele será reaproveitado.'}
                </p>

                <div className="flex flex-col gap-4">
                    {isReopen && (
                        <div className="px-3 py-2 rounded-lg bg-base-200/60 border border-base-300">
                            <div className="text-sm font-semibold truncate">
                                {name || 'Contato'}
                            </div>
                            <div className="text-xs text-base-content/60">
                                {formatPhoneNumber(phoneNumber)}
                            </div>
                        </div>
                    )}

                    {!isReopen && (
                        <div
                            ref={searchBoxRef}
                            className="form-control w-full relative"
                        >
                        <span className="label-text font-medium mb-1">
                            Buscar cliente
                        </span>
                        {selectedClient ? (
                            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-secondary/10 border border-secondary/30">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold truncate">
                                        {selectedClient.name}
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                        {formatPhoneNumber(
                                            selectedClient.whatsAppNumber || ''
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs btn-circle"
                                    onClick={clearClient}
                                    disabled={submitting}
                                    title="Trocar"
                                >
                                    <MdClose className="text-base" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <MdSearch className="absolute z-10 left-2.5 top-1/2 -translate-y-1/2 text-base-content/40 text-sm z-10" />
                                    <input
                                        type="text"
                                        className="input input-bordered input-sm w-full pl-8"
                                        placeholder="Buscar por nome ou telefone"
                                        value={searchInput}
                                        onChange={e => {
                                            setSearchInput(e.target.value)
                                            setShowResults(true)
                                        }}
                                        onFocus={() => setShowResults(true)}
                                        disabled={submitting}
                                        autoComplete="off"
                                    />
                                </div>
                                {showResults &&
                                    searchInput.trim().length >= 2 && (
                                        <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                            {searching ? (
                                                <div className="flex justify-center py-3">
                                                    <span className="loading loading-spinner loading-xs" />
                                                </div>
                                            ) : searchResults.length === 0 ? (
                                                <div className="px-3 py-2 text-xs text-base-content/50">
                                                    Nenhum cliente encontrado.
                                                    Preencha telefone e nome
                                                    abaixo para iniciar com novo
                                                    contato.
                                                </div>
                                            ) : (
                                                searchResults.map(c => (
                                                    <button
                                                        key={c.id}
                                                        type="button"
                                                        className="w-full text-left px-3 py-2 hover:bg-base-200 border-b border-base-300/40 last:border-b-0"
                                                        onClick={() =>
                                                            pickClient(c)
                                                        }
                                                    >
                                                        <div className="text-sm font-medium truncate">
                                                            {c.name}
                                                        </div>
                                                        <div className="text-xs text-base-content/60">
                                                            {formatPhoneNumber(
                                                                c.whatsAppNumber ||
                                                                    ''
                                                            )}
                                                        </div>
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                            </>
                        )}
                        </div>
                    )}

                    {!isReopen && !selectedClient && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="form-control w-full">
                                <span className="label-text font-medium mb-1">
                                    Telefone (com DDD)
                                </span>
                                <input
                                    type="tel"
                                    className="input input-bordered input-sm w-full"
                                    placeholder="Ex: (11) 99999-9999"
                                    value={phoneNumber}
                                    onChange={e =>
                                        setPhoneNumber(e.target.value)
                                    }
                                    disabled={submitting}
                                />
                            </label>

                            <label className="form-control w-full">
                                <span className="label-text font-medium mb-1">
                                    Nome do contato
                                </span>
                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-full"
                                    placeholder="Nome para identificar na lista"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    disabled={submitting}
                                />
                            </label>
                        </div>
                    )}

                    <label className="form-control w-full">
                        <span className="label-text font-medium mb-1">
                            Departamento
                        </span>
                        <select
                            className="select select-bordered select-sm w-full"
                            value={departmentId ?? ''}
                            onChange={e =>
                                setDepartmentId(
                                    e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : null
                                )
                            }
                            disabled={submitting || loadingDeps || isReopen}
                        >
                            <option value="">
                                {loadingDeps
                                    ? 'Carregando...'
                                    : departments.length === 0
                                      ? 'Nenhum departamento com WhatsApp configurado'
                                      : 'Selecione um departamento'}
                            </option>
                            {departments.map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    {isWhatsappWeb ? (
                        <label className="form-control w-full">
                            <span className="label-text font-medium mb-1">
                                Mensagem
                            </span>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                rows={4}
                                placeholder="Escreva a mensagem que abrirá a conversa"
                                value={freeText}
                                onChange={e => setFreeText(e.target.value)}
                                disabled={submitting}
                            />
                            <span className="label-text-alt text-base-content/60 mt-1">
                                Sem janela de 24 horas neste canal — não é
                                preciso template aprovado.
                            </span>
                        </label>
                    ) : (
                        <>
                            <label className="form-control w-full">
                                <span className="label-text font-medium mb-1">
                                    Template
                                </span>
                                <select
                                    className="select select-bordered select-sm w-full"
                                    value={selectedTemplateId ?? ''}
                                    onChange={e => {
                                        const id = e.target.value
                                            ? parseInt(e.target.value, 10)
                                            : null
                                        setSelectedTemplateId(id)
                                        setTemplateVars({})
                                    }}
                                    disabled={submitting || loadingTpls}
                                >
                                    <option value="">
                                        {loadingTpls
                                            ? 'Carregando...'
                                            : templates.length === 0
                                              ? 'Nenhum template aprovado disponível'
                                              : 'Selecione um template'}
                                    </option>
                                    {templates.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.friendlyName}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {selectedTemplate && (
                                <>
                                    {isTokenized ? (
                                        <div className="text-xs text-base-content/60 bg-info/10 border border-info/20 rounded-lg p-2">
                                            As variáveis deste template são preenchidas
                                            automaticamente no envio (nome do cliente,
                                            atendente, empresa, etc.).
                                        </div>
                                    ) : (
                                        requiredVars.map(idx => (
                                            <label
                                                key={idx}
                                                className="form-control w-full"
                                            >
                                                <span className="label-text text-sm mb-1">
                                                    Campo {idx}
                                                </span>
                                                <input
                                                    type="text"
                                                    className="input input-bordered input-sm w-full"
                                                    value={
                                                        templateVars[String(idx)] ?? ''
                                                    }
                                                    onChange={e =>
                                                        setTemplateVars(v => ({
                                                            ...v,
                                                            [String(idx)]:
                                                                e.target.value,
                                                        }))
                                                    }
                                                    disabled={submitting}
                                                />
                                            </label>
                                        ))
                                    )}

                                    <div>
                                        <span className="label-text text-sm mb-1 block">
                                            Pré-visualização
                                        </span>
                                        <div className="bg-base-200 rounded-lg p-3 text-sm whitespace-pre-wrap">
                                            {renderPreview()}
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting}
                    >
                        {submitting ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : isReopen ? (
                            'Reabrir e enviar'
                        ) : (
                            'Iniciar atendimento'
                        )}
                    </button>
                </div>
            </div>
            <div
                className="modal-backdrop"
                onClick={() => !submitting && onClose()}
            />
        </div>
    )
}
